import { isReportableCourseStatus } from '../tics/ticsEventCourseVersionPolicy.js';

/**
 * Shared step labels/indices for coordinator + training-manager event create wizard.
 * Keep Learning Lab playbooks / publish fallback in sync via these constants.
 */
export const EVENT_CREATE_WIZARD_STEPS = [
  'Basics',
  'When & where',
  'Registration & pricing',
  'Delivery & staff',
  'Allocation Settings',
  'Review & Publish',
] as const;

export type EventCreateWizardStepLabel = (typeof EVENT_CREATE_WIZARD_STEPS)[number];

export const EVENT_CREATE_WIZARD_STEP_COUNT = EVENT_CREATE_WIZARD_STEPS.length;

export const EVENT_CREATE_WIZARD_BASICS_INDEX = 0;
export const EVENT_CREATE_WIZARD_SCHEDULE_INDEX = 1;
export const EVENT_CREATE_WIZARD_REGISTRATION_INDEX = 2;
export const EVENT_CREATE_WIZARD_DELIVERY_INDEX = 3;
export const EVENT_CREATE_WIZARD_ALLOCATION_INDEX = 4;
export const EVENT_CREATE_WIZARD_REVIEW_INDEX = 5;
export const EVENT_CREATE_WIZARD_LAST_INDEX = EVENT_CREATE_WIZARD_REVIEW_INDEX;

/** EventForm panes for wizard steps 0–3 (and gallery on review). */
export type EventFormWizardPane =
  | 'all'
  | 'basics'
  | 'schedule'
  | 'registration'
  | 'delivery'
  | 'gallery';

export const EVENT_CREATE_WIZARD_PANE_BY_STEP: Record<
  0 | 1 | 2 | 3,
  Exclude<EventFormWizardPane, 'all' | 'gallery'>
> = {
  0: 'basics',
  1: 'schedule',
  2: 'registration',
  3: 'delivery',
};

export function isEventFormEarlyWizardStep(stepIndex: number): boolean {
  return stepIndex >= 0 && stepIndex <= EVENT_CREATE_WIZARD_DELIVERY_INDEX;
}

export interface EventCreateWizardScheduleFields {
  venue_id?: number;
  category_id?: number;
  /** National Course Directory link — satisfies classification when category is unset. */
  course_catalog_id?: number | null;
  course_catalog_version_id?: number | null;
  start_date?: Date | string | null;
  end_date?: Date | string | null;
  start_time?: string;
  end_time?: string;
  audience_ids?: number[];
}

/** Step 1 (When & where) gate — keep in sync with coordinator event create wizard. */
export function isEventCreateWizardScheduleStepComplete(
  formData: EventCreateWizardScheduleFields,
): boolean {
  const hasClassification =
    (formData.category_id != null && formData.category_id > 0) ||
    (formData.course_catalog_id != null && Number(formData.course_catalog_id) > 0) ||
    (formData.course_catalog_version_id != null &&
      Number(formData.course_catalog_version_id) > 0);
  return !!(
    formData.venue_id &&
    formData.venue_id > 0 &&
    hasClassification &&
    formData.start_date &&
    formData.end_date &&
    formData.start_time &&
    formData.end_time &&
    formData.audience_ids &&
    formData.audience_ids.length > 0
  );
}

export function eventWasPublishedWhenLoaded(status: unknown): boolean {
  return status === 2 || status === true;
}

/** Registration step + publish — draft autosave must not bypass fee acceptance. */
export function isEventCreateWizardFeeGateSatisfied(input: {
  isExternal: boolean;
  feeAccepted: boolean;
  eventWasPublishedOnLoad: boolean;
}): boolean {
  if (input.isExternal) {
    return true;
  }
  return input.feeAccepted || input.eventWasPublishedOnLoad;
}

export function requiresEventFeeAcceptanceToPublish(input: {
  isExternal: boolean;
  feeAccepted: boolean;
  eventWasPublishedOnLoad: boolean;
  publishing: boolean;
}): boolean {
  return (
    input.publishing &&
    !input.isExternal &&
    !input.feeAccepted &&
    !input.eventWasPublishedOnLoad
  );
}

/** Human-readable reason Next/Publish is disabled (empty when step is valid). */
export function getEventCreateWizardStepBlockReason(input: {
  step: number;
  title?: string;
  description?: string;
  shortDescription?: string;
  isExternal?: boolean;
  externalUrl?: string | null;
  venueId?: number;
  categoryId?: number;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  startTime?: string;
  endTime?: string;
  audienceIds?: number[];
  registrationOpenDate?: Date | string | null;
  registrationCloseDate?: Date | string | null;
  feeAccepted: boolean;
  eventWasPublishedOnLoad: boolean;
  willBeReportedToCommission?: boolean;
  courseCatalogId?: number | null;
  courseCatalogVersionId?: number | null;
  /** Status of the linked course; a `draft` link cannot back a reportable event. */
  courseCatalogStatus?: string | null;
  isLabMode?: boolean;
  isRestricted?: boolean;
  allocations?: Array<{ allocatedSeats: number }>;
  connectBlocksPublish?: boolean;
  /** Optional precomputed compliance errors from validateEventPublishCompliance */
  publishComplianceMessages?: string[];
}): string | null {
  switch (input.step) {
    case EVENT_CREATE_WIZARD_BASICS_INDEX:
      if (!(input.title && input.description)) {
        return 'Enter an event title and description to continue.';
      }
      if (input.publishComplianceMessages?.length) {
        return input.publishComplianceMessages.join(' ');
      }
      if (input.isExternal && !input.externalUrl?.trim()) {
        return 'Enter an external registration URL to continue.';
      }
      return null;
    case EVENT_CREATE_WIZARD_SCHEDULE_INDEX:
      if (
        !isEventCreateWizardScheduleStepComplete({
          venue_id: input.venueId,
          category_id: input.categoryId,
          course_catalog_id: input.courseCatalogId,
          course_catalog_version_id: input.courseCatalogVersionId,
          start_date: input.startDate,
          end_date: input.endDate,
          start_time: input.startTime,
          end_time: input.endTime,
          audience_ids: input.audienceIds,
        })
      ) {
        return 'Complete venue, classification (course or category), dates, times, and at least one target audience.';
      }
      return null;
    case EVENT_CREATE_WIZARD_REGISTRATION_INDEX:
      if (input.isExternal) {
        return input.externalUrl?.trim()
          ? null
          : 'Enter an external registration URL to continue.';
      }
      if (!input.registrationOpenDate || !input.registrationCloseDate) {
        return 'Set registration open and close dates to continue.';
      }
      if (
        !isEventCreateWizardFeeGateSatisfied({
          isExternal: !!input.isExternal,
          feeAccepted: input.feeAccepted,
          eventWasPublishedOnLoad: input.eventWasPublishedOnLoad,
        })
      ) {
        return 'Accept the platform fee terms to continue.';
      }
      return null;
    case EVENT_CREATE_WIZARD_DELIVERY_INDEX:
      if (input.isLabMode) return null;
      if (input.willBeReportedToCommission) {
        const hasCatalog =
          (input.courseCatalogId != null && Number(input.courseCatalogId) > 0) ||
          (input.courseCatalogVersionId != null && Number(input.courseCatalogVersionId) > 0);
        if (!hasCatalog) {
          return 'Link a National Course Directory course on Basics — required when this event will be reported to the state commission.';
        }
        if (!isReportableCourseStatus(input.courseCatalogStatus)) {
          return 'The linked course is still a draft awaiting directory review. Pick a published course, or turn off commission reporting for this event.';
        }
      }
      return null;
    case EVENT_CREATE_WIZARD_ALLOCATION_INDEX:
      if (input.isExternal || !input.isRestricted) return null;
      if (
        !input.allocations?.length ||
        input.allocations.some((a) => !(a.allocatedSeats > 0))
      ) {
        return 'Add at least one allocation with seats when registration is restricted.';
      }
      return null;
    case EVENT_CREATE_WIZARD_REVIEW_INDEX:
      if (input.isLabMode) return null;
      if (input.connectBlocksPublish) {
        return 'Finish Stripe Connect payout setup before publishing a paid event.';
      }
      if (input.publishComplianceMessages?.length) {
        return input.publishComplianceMessages.join(' ');
      }
      return null;
    default:
      return null;
  }
}

